import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Dialog } from '@components/dialog';
import {
  SubjectivityFormContent,
  SubjectivityFormRef,
} from '@components/Subjectivities/quote-subjectivities/SubjectivityForm';
import { SubjectivityItem } from '@services/subjectivity';
import { SubjectivitiesTab } from '@pages/QuoteDetails/sidebarItems/subjectivities/SubjectivitiesPanel';
import { useQuote } from '@services/quotes';
import { quoteStatusTerminal } from '@models/quotes';

interface SubjectivityDialogProps {
  open: boolean;
  onClose: () => void;
  subjectivity?: SubjectivityItem | null;
  setSubjectivity?: (subjectivity: SubjectivityItem | null) => void;
}

export const SubjectivityDialog = ({ open, onClose, subjectivity, setSubjectivity }: SubjectivityDialogProps) => {
  const { quoteId } = useParams<'quoteId'>();
  const { quote } = useQuote(quoteId ?? '');
  const formRef = useRef<SubjectivityFormRef>(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  const isQuoteTerminal = !!quote?.status.name && quoteStatusTerminal.includes(quote?.status.name);
  const isEditMode = !!subjectivity;

  const handleClose = () => {
    if (setSubjectivity) {
      setSubjectivity(null);
    }
    onClose();
  };

  const handleSubmit = () => {
    formRef.current?.submit();
  };

  // For edit mode, don't render if no subjectivity is provided
  if (isEditMode && !subjectivity) return null;

  const title = isEditMode ? `Edit Subjectivity: ${subjectivity?.label}` : 'Add Subjectivity to Quote';

  const primaryBtnText = isEditMode ? 'Update' : 'Create';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={title}
      handleCloseDialog={handleClose}
      primaryBtnText={primaryBtnText}
      secondaryBtnText='Cancel'
      primaryBtnAction={handleSubmit}
      secondaryBtnAction={handleClose}
      isPrimaryBtnDisabled={isSubmitDisabled || isQuoteTerminal}
      size='small'
    >
      <SubjectivityFormContent
        ref={formRef}
        selectedTab={isEditMode ? SubjectivitiesTab.EditSubjectivity : SubjectivitiesTab.NewSubjectivity}
        selectedItem={subjectivity ?? undefined}
        isBroker={false}
        defaultAddMode='single'
        isDialogMode
        onClose={handleClose}
        onSuccess={isEditMode ? handleClose : undefined}
        onSubmitDisabledChange={(isDisabled) => setIsSubmitDisabled(isDisabled)}
        quoteId={quoteId}
      />
    </Dialog>
  );
};
